import React, { useEffect, useMemo } from 'react';
import { useRouteMatch, generatePath, useHistory, Link } from 'react-router-dom';
import useDataSource from 'hooks/useDataSource';
import RoutedLayout from 'components/Layout/RoutedLayout';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import useUser from 'hooks/useUser';
import { EDIT_LICENSE } from 'shared/constants/permissions';
import { hasModuleLicense } from 'shared/utils/license';
import { Module } from 'shared/services/prisma/types';
import { Button, Result } from 'antd';
import Loading from 'components/Loading';
import messages from '../../Results/messages';
import routes from './routes';

const extraFields = `
  surveyGroups { 
    id 
    title 
    surveys {
      id
      version
      primary
    }
  }
`;

const GET_AVAILABLE_TABS = gql`
  query Results_availableResultTabs($surveyGroupId: ID!, $displayedSurveyId: ID!, $type: SurveyType) {
    availableResultTabs(surveyGroupId: $surveyGroupId, displayedSurveyId: $displayedSurveyId, type: $type)
  }
`;

function getDisplayedSurveyId(surveys, storedDisplayedSurveyId) {
  let displayedSurvey;

  for (const survey of surveys) {
    if (survey.id === storedDisplayedSurveyId) {
      displayedSurvey = survey;
      break;
    }

    if (!displayedSurvey?.version || (survey.version > displayedSurvey.version && survey.primary)) {
      displayedSurvey = survey;
    }
  }

  return displayedSurvey?.id;
}

function getTab(surveyMenuItems, storedTag) {
  return surveyMenuItems.find(c => c.path === storedTag)?.path || surveyMenuItems[0]?.path;
}

const Surveys = () => {
  const history = useHistory();
  const { isSuperUser } = useUser();
  const { path, params } = useRouteMatch('/data-sources/:dataSourceId/:module/:page?/:surveyGroupId?/:displayedSurveyId?/:tab?');
  const { surveyGroupId, displayedSurveyId, page } = params;
  const { dataSource, dataSourceId, loading: dataSourceLoading, hasDataSourcePermission } = useDataSource({ extraFields });
  const surveyGroup = dataSource?.surveyGroups.find(sg => sg.id === surveyGroupId);
  const displayedSurvey = surveyGroup?.surveys.find(s => s.id === displayedSurveyId);
  const type = displayedSurvey?.type;

  const hasSurveys = hasModuleLicense(dataSource?.activeLicense, Module.UxSurveys) || isSuperUser;

  const { loading: tabsLoading, data: { availableResultTabs = [] } = {} } = useQuery(GET_AVAILABLE_TABS, {
    variables: { surveyGroupId, displayedSurveyId, type },
    skip: !surveyGroupId || !displayedSurveyId || !hasSurveys,
  });

  const surveyMenuItems = useMemo(() => availableResultTabs?.filter(c => c.content.length).map(c => ({
    path: c.name,
    label: <FormattedMessage {...messages[c.name]} />,
    link: generatePath(path, { ...params, tab: c.name }),
  })), [availableResultTabs, params, path]);

  useEffect(() => {
    if (surveyGroup && !displayedSurveyId) {
      history.replace(generatePath(path, { ...params, displayedSurveyId: getDisplayedSurveyId(surveyGroup.surveys, localStorage.getItem(`displayedSurveyId-${surveyGroupId}`)) }));
    }
  }, [surveyGroup, params, path, history]);

  useEffect(() => {
    if (surveyMenuItems?.length && page === 'results') {
      if (params.tab) {
        if (params.tab === surveyMenuItems[0]?.path) {
          localStorage.removeItem(`surveyTab-${surveyGroupId}`);
        } else {
          localStorage.setItem(`surveyTab-${surveyGroupId}`, params.tab);
        }
      } else {
        history.replace(generatePath(path, { ...params, tab: getTab(surveyMenuItems, localStorage.getItem(`surveyTab-${surveyGroupId}`)) }));
      }
    }
  }, [surveyMenuItems, params, path, history]);

  const dynamicRoutes = useMemo(() => routes.map(route => {
    if (route.path === 'results' || route.path === 'edit') {
      const surveyRoutes = dataSource?.surveyGroups.map(sg => ({
        path: sg.id,
        label: sg.title,
        children: sg.id === surveyGroupId && route.path === 'results' ? surveyMenuItems : null,
      })) || [];

      return {
        ...route,
        children: surveyRoutes,
      };
    }
    return route;
  }), [routes, dataSource, surveyGroupId, surveyMenuItems]);

  const loading = dataSourceLoading || tabsLoading;

  if (loading) {
    return <Loading />;
  }

  if (!hasSurveys) {
    return (
      <Result
        status="info"
        title="No license"
        subTitle="You need to have a license for surveys to view this page."
        extra={hasDataSourcePermission(EDIT_LICENSE) ? (
          <Link to={`/data-sources/${dataSourceId}/settings/license`}>
            <Button type="primary">Upgrade license</Button>
          </Link>
        ) : (
          <>Ask the owner to setup a license.</>
        )}
      />
    );
  }

  return (
    <RoutedLayout
      className={`module-${params.module}`}
      routes={dynamicRoutes}
      sidebar
    />
  );
};

export default Surveys;
